/**
 * A que BCF pertence uma sincronização.
 *
 * O servidor guarda os tópicos de cada BCF em separado, pelo
 * identificador de projecto do próprio ficheiro: o `ProjectId` do
 * `project.bcfp`. Viaja com o ficheiro, e quem importar o mesmo BCF
 * sincroniza com os mesmos tópicos.
 *
 * Um ficheiro sem identificador recebe um local, derivado do id do BCF
 * neste browser. Sincroniza-se na mesma, mas só este browser o encontra:
 * outra pessoa que importe o mesmo ficheiro fica com outro identificador.
 * O painel di-lo.
 */

export interface ProjetoBcf {
  /** O identificador enviado ao servidor. */
  id: string
  /** O nome do BCF, para a interface. */
  nome: string
  /** Verdadeiro quando o identificador vem do ficheiro, e por isso é o
   *  mesmo para quem importar o mesmo BCF. */
  partilhavel: boolean
  /** O id da partilha, quando o BCF veio de outro workspace
   *  ("Partilhados comigo"). */
  partilhaId?: string | null
}

/** O mínimo de um BCF carregado de que isto precisa. */
export interface BcfParaProjeto {
  localId: string
  name: string
  sourceProjectId: string | null
  versions: readonly { parsed: { sourceProjectId: string | null } }[]
  partilhaId?: string | null
}

export const PREFIXO_LOCAL = 'local:'

export function projetoDoBcf(bcf: BcfParaProjeto): ProjetoBcf {
  // Um BCF partilhado de outro workspace sincroniza-se pela partilha,
  // com a casa dele (ver backend/modules/bcf/sync_router.py).
  if (bcf.partilhaId) {
    return {
      id: `partilha:${bcf.partilhaId}`,
      nome: bcf.name,
      partilhavel: true,
      partilhaId: bcf.partilhaId,
    }
  }
  const ultima = bcf.versions[bcf.versions.length - 1]
  const doFicheiro = bcf.sourceProjectId ?? ultima?.parsed.sourceProjectId ?? null
  return doFicheiro
    ? { id: doFicheiro, nome: bcf.name, partilhavel: true }
    : { id: PREFIXO_LOCAL + bcf.localId, nome: bcf.name, partilhavel: false }
}

/** A que BCF se refere a sincronização, dito no painel. */
export function textoDoProjeto(projeto: {
  nome: string
  partilhavel: boolean
  partilhaId?: string | null
}): string {
  if (projeto.partilhaId) {
    return `BCF «${projeto.nome}», partilhado consigo a partir de outro workspace. Sincroniza com quem tem acesso a ele.`
  }
  return projeto.partilhavel
    ? `Sincronização do BCF «${projeto.nome}», com quem tem acesso a ele (ver Partilhar com).`
    : `Sincronização do BCF «${projeto.nome}». O ficheiro de origem não declara identificador de `
      + 'projecto: para outra pessoa sincronizar este BCF, deve importar o .bcfzip exportado a partir '
      + 'daqui, que já o inclui.'
}
